/**
 * Renderer - Three.js Wrapper
 *
 * Now delegates to SharedRenderer for the WebGL context.
 * Owns its own Scene + Camera and registers as a "view".
 */

import * as THREE from 'three';
import { SharedRenderer, QUALITY_SETTINGS } from './SharedRenderer';
import { renderScheduler } from './RenderScheduler';

export type { RenderQuality } from './SharedRenderer';
import type { RenderQuality } from './SharedRenderer';

// SpotLight shadow-camera focus (<1 narrows the shadow frustum without touching
// the light cone). The served scene's spot sits ~37u up with a 60° cone, so its
// floor footprint is ~2× the dice play area; 0.6 pulls the shadow frustum back
// near the floor (footprint ~14u vs the ~10u play radius — still covered with
// margin), packing more texels onto the play area. Lower = tighter but risks
// clipping shadows of dice near the floor edge; raise toward 1 if that happens.
const SPOT_SHADOW_FOCUS = 0.6;

// Spot shadow-camera near/far. The served doc leaves these unset → three.js
// defaults to 0.5/500, a 1000:1 range that wrecks VSM depth precision (it bands
// the floor). 20/55 brackets the actual casters (spot ~37u up; wall/dice sit
// ~20–55u from it), giving VSM the precision it needs. (far may be re-driven to
// light.distance by SpotLightShadow.updateMatrices — still a healthy ratio.)
const SPOT_SHADOW_NEAR = 20;
const SPOT_SHADOW_FAR = 55;

// VSM blur/bias for the served scene's shadow caster (type set in SharedRenderer).
// VSM blurs the shadow map like a texture → smooth penumbra. Values dialed in
// on-device via window.__shadow(...): radius=blur width, blurSamples=blur quality,
// bias/normalBias kill VSM light-bleed (faint/leaking shadow) without detaching
// the contact shadow. Dice are receiveShadow=false so there's no pip acne to fight.
const SHADOW_RADIUS = 2;
const SHADOW_BLUR_SAMPLES = 8;
const SHADOW_BIAS = -0.0008;
const SHADOW_NORMAL_BIAS = 0.05;

export interface RendererConfig {
  canvas?: HTMLCanvasElement;
  container?: HTMLElement;
  quality?: RenderQuality;
  antialias?: boolean;
  shadows?: boolean;
  maxDpr?: number;
}

export class Renderer {
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;

  private shared: SharedRenderer;
  private container: HTMLElement;
  private quality: RenderQuality;
  private viewId: string | null = null;
  private resizeObserver: ResizeObserver | null = null;

  // Performance tracking
  private renderCalls = 0;
  private triangles = 0;

  constructor(config: RendererConfig = {}) {
    this.shared = SharedRenderer.instance;
    this.quality = config.quality ?? 'high';
    this.container = config.container ?? document.body;

    // Create scene
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x0a0a0a);

    // Create camera
    // near=1.0 (not 0.1): the camera never gets within ~6 units of any surface
    // (minHeight 6, betting anchor y=10, zoom-to-fit only ever dollies *back*),
    // so a near of 1.0 has huge margin and takes the near:far ratio from
    // 1:10000 to 1:1000. On the Android WebView's 16-bit depth buffer that
    // ~10x precision gain is what kills the coplanar pip/body z-fighting and
    // shadow acne that desktop's 24-bit buffer hides. far stays 1000 so the
    // dynamic zoom-out can never clip distant geometry.
    this.camera = new THREE.PerspectiveCamera(
      50,
      this.container.clientWidth / this.container.clientHeight,
      1.0,
      1000
    );
    this.camera.position.set(0, 10, 15);
    this.camera.lookAt(0, 0, 0);

    // Register as a view on the shared renderer.
    // `manual: true` — the Engine game loop renders this view itself (via
    // render(), after physics interpolation). The SharedRenderer's own rAF
    // loop must NOT also draw it, or the scene renders twice per frame.
    this.viewId = this.shared.addView({
      scene: this.scene,
      camera: this.camera,
      container: this.container,
      priority: 0,
      manual: true,
    });

    // Watch for resizes
    this.setupResizeObserver();
  }

  private setupResizeObserver(): void {
    this.resizeObserver = new ResizeObserver(() => {
      this.handleResize();
    });
    this.resizeObserver.observe(this.container);
    window.addEventListener('resize', this.handleResize.bind(this));
  }

  private handleResize(): void {
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;
    if (width === 0 || height === 0) return;

    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();

    // Redraw promptly on resize / orientation change instead of waiting for the
    // idle heartbeat (a stale frame mid-rotation looks janky).
    renderScheduler.pulse();

    // SharedRenderer handles gl.setSize via resizeIfNeeded
  }

  /** Get the underlying WebGLRenderer (for compatibility). */
  get renderer(): THREE.WebGLRenderer {
    return this.shared.gl;
  }

  /**
   * Render the scene. Called by Engine's game loop.
   * The SharedRenderer has its own rAF loop for UI views,
   * but the game Engine calls render() directly during its game loop.
   */
  render(): void {
    // Skip while the WebGL context is lost — rendering/disposing into a dead
    // context throws "object does not belong to this context" and crashes.
    if (this.shared.isContextLost) return;

    const gl = this.shared.gl;
    const w = this.container.clientWidth;
    const h = this.container.clientHeight;
    if (w === 0 || h === 0) return;

    // Ensure canvas is in our container and sized correctly
    if (gl.domElement.parentElement !== this.container) {
      gl.domElement.style.display = 'block';
      gl.domElement.style.width = '100%';
      gl.domElement.style.height = '100%';
      this.container.appendChild(gl.domElement);
    }

    // Resize if needed
    const canvas = gl.domElement;
    if (canvas.clientWidth !== w || canvas.clientHeight !== h) {
      gl.setPixelRatio(this.shared.pixelRatio);
      gl.setSize(w, h, false);
    }

    // On-demand shadow pass (shadowMap.autoUpdate is false, see SharedRenderer):
    // re-render the VSM map only when a caster moved or the 150ms staleness
    // ceiling hit. three resets needsUpdate to false after the shadow pass.
    if (renderScheduler.shouldRenderShadows()) {
      gl.shadowMap.needsUpdate = true;
    }

    gl.clear();
    gl.render(this.scene, this.camera);

    // Track stats
    const info = gl.info;
    this.renderCalls = info.render.calls;
    this.triangles = info.render.triangles;
  }

  setQuality(quality: RenderQuality): void {
    this.quality = quality;
    this.shared.setQuality(quality);

    // Update shadow map size on the served scene's lights. No shadow bias is
    // applied: a constant/normal bias eats the contact shadow when a die rests
    // on the floor and pulls shadows off their contact line. Pip shadow-acne is
    // solved by the dice being receiveShadow=false (see DiceAssetManager), so
    // default bias (0) gives the cleanest result.
    const settings = QUALITY_SETTINGS[quality];
    this.scene.traverse((obj) => {
      if (obj instanceof THREE.Light && obj.shadow) {
        obj.shadow.mapSize.setScalar(settings.shadowMapSize);

        if (obj.castShadow) {
          // VSM blur/bias (type set in SharedRenderer) → smooth penumbra without
          // the PCF stair-step. Applied to whatever the scene's caster is — the
          // served scene casts from a high-mounted SpotLight, but a directional
          // caster (fallback / other docs) gets the same softening.
          obj.shadow.radius = SHADOW_RADIUS;
          obj.shadow.blurSamples = SHADOW_BLUR_SAMPLES;
          obj.shadow.bias = SHADOW_BIAS;
          obj.shadow.normalBias = SHADOW_NORMAL_BIAS;
          // Spot only: narrow the shadow frustum onto the play area (texels land
          // where the dice are; doesn't touch the light cone / lighting) and tighten
          // the shadow-camera near/far so VSM has the depth precision it needs.
          if (obj instanceof THREE.SpotLight) {
            obj.shadow.focus = SPOT_SHADOW_FOCUS;
            obj.shadow.camera.near = SPOT_SHADOW_NEAR;
            obj.shadow.camera.far = SPOT_SHADOW_FAR;
            obj.shadow.camera.updateProjectionMatrix();
          }
        }

        obj.shadow.map?.dispose();
        obj.shadow.map = null;
      }
    });

    // The shadow map was just disposed — force a rebuild on the next drawn frame.
    renderScheduler.markShadowsDirty();
  }

  getStats() {
    return {
      renderCalls: this.renderCalls,
      triangles: this.triangles,
      quality: this.quality,
      dpr: this.shared.gl.getPixelRatio(),
    };
  }

  getBettingQualitySettings() {
    return this.shared.getBettingQualitySettings();
  }

  get canvas(): HTMLCanvasElement {
    return this.shared.canvas;
  }

  getSize(): { width: number; height: number } {
    return {
      width: this.container.clientWidth,
      height: this.container.clientHeight,
    };
  }

  dispose(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    // Remove our view from the shared renderer (don't dispose the shared renderer itself)
    if (this.viewId) {
      this.shared.removeView(this.viewId);
      this.viewId = null;
    }
  }
}
